/* eslint-disable @typescript-eslint/no-unused-vars */
import { useState, useEffect } from 'react'
import { mdiArrowLeft } from '@mdi/js'
import Icon from '@mdi/react'
import { useIntl, FormattedMessage } from 'react-intl'
import { HomeId } from '@racepointenergy/swc/scs/types'
import { generatePath, RouteComponentProps, useHistory } from 'react-router-dom'
import { stateEntityAdapter } from '@racepointenergy/swc/scs/features/host-states'
import { dealerMonitorStates } from '@racepointenergy/swc/scs/dealer-monitor-states'

import { WhiteBlock } from 'components/Layout'
import { messages } from '../messages'
import {
  BreakerHeader,
  PanelInfoColumn,
  PanelInfoHeaderText,
  PanelInfoText,
  PanelInfoRow,
  NavBack,
  BreakerBlock,
  BreakerGrid,
} from '../styles'
import { SpinnerCentered } from 'components/Loader'
import { useAppSelector, useAppDispatch } from 'app/hooks'
import { H1 } from 'components/Typography'
import theme from 'theme_'
import ROUTES from 'app/routes'
import { currentRootDomainIdSelector } from 'features/rootDomains/selectors'
import { homeActiveConfigEnergySelector } from 'features/home/selectors'
import {
  fetchHomeActiveConfigEnergyThunk,
  homeActiveConfigEnergyActions,
} from 'features/home/components/HomeEnergySystem/slice'
import { Module, Panel } from 'features/home/types'

interface BreakerInfoProps
  extends RouteComponentProps<{
    homeId: HomeId
    panelId: string
    breakerId: string
  }> {}

const hostStateInitialState = stateEntityAdapter.getInitialState()

function BreakerInfo({ match }: BreakerInfoProps) {
  const { formatMessage } = useIntl()
  const history = useHistory()
  const dispatch = useAppDispatch()

  const currentRootId = useAppSelector(currentRootDomainIdSelector)

  const [isMobile, setIsMobile] = useState<boolean>(false)
  const [breaker, setBreaker] = useState<Module>()
  const [circuitName, setCircuitName] = useState('-')
  const [panelData, setPanelData] = useState<Panel | null>(null)
  const [panelSlots, setPanelSlots] = useState('-')

  const { homeId, panelId, breakerId } = match.params

  const {
    data = hostStateInitialState,
    isError,
    isFetching,
    error,
  } = dealerMonitorStates.useQuery({ id: homeId })

  const isHomeActiveConfigEnergyLoaded = useAppSelector(
    homeActiveConfigEnergySelector.isFinished
  )
  const homeActiveConfigEnergy = useAppSelector(
    homeActiveConfigEnergySelector.data
  )

  const handleResize = () => {
    setIsMobile(window.matchMedia(theme.devices.mobile).matches)
  }

  useEffect(() => {
    window.addEventListener('resize', handleResize)
    handleResize()

    return () => {
      window.removeEventListener('resize', handleResize)
      dispatch(homeActiveConfigEnergyActions.clear())
    }
  }, [dispatch, homeId])

  useEffect(() => {
    if (homeId && !isHomeActiveConfigEnergyLoaded) {
      dispatch(fetchHomeActiveConfigEnergyThunk(homeId))
    }
  }, [dispatch, homeId, isHomeActiveConfigEnergyLoaded])
  useEffect(() => {
    if (homeActiveConfigEnergy) {
      const dumbHardware =
        homeActiveConfigEnergy.energyConfig?.panelConfig?.breakerPanelModules?.find(
          (device) => device.id === breakerId
        )

      if (dumbHardware) {
        const ch =
          homeActiveConfigEnergy.energyConfig?.panelConfig?.breakerPanelModules?.find(
            (device) =>
              device.channels?.find(
                (channel) => channel?.circuitInId === dumbHardware.id
              )
          )

        if (ch?.channels?.length) {
          const uid = ch.channels.find(
            (channel) => channel?.circuitInId === dumbHardware.id
          )?.uid

          if (uid) {
            const hwid = homeActiveConfigEnergy?.lightingDevices
              ?.find((device) =>
                device.smartHardware.find((hw) => hw.hwUID === uid)
              )
              ?.smartHardware.find((hw) => hw.hwUID === uid)?.smartHardwareID

            if (hwid) {
              const cName = homeActiveConfigEnergy?.groups?.find((group) =>
                group.smartHardwareIDs.includes(hwid)
              )?.name

              if (cName) {
                setCircuitName(cName)
              }
            }
          }
        }
        setBreaker(dumbHardware)
      }
    }
  }, [homeActiveConfigEnergy, breakerId])

  useEffect(() => {
    if (
      homeActiveConfigEnergy?.energyConfig?.panelConfig?.breakerPanels?.length
    ) {
      const e =
        homeActiveConfigEnergy.energyConfig?.panelConfig.breakerPanels.find(
          (panel) => panel.id === panelId
        )
      if (e) {
        setPanelData(e)
      }
    }
  }, [homeActiveConfigEnergy, setPanelData, panelId, panelData?.slotsNumber])

  const onBackClick = () => {
    const devicesPath = generatePath(ROUTES.breakerPanel, {
      rootDomainId: currentRootId || '',
      homeId: homeId || ' ',
      panelId: panelId || '',
    })

    return history.push(devicesPath)
  }

  return (
    <div>
      <NavBack onClick={onBackClick}>
        <Icon path={mdiArrowLeft} size={1} />
        <FormattedMessage {...messages.backToDevices} />
      </NavBack>
      <WhiteBlock>
        {isFetching ? (
          <SpinnerCentered />
        ) : (
          <>
            <BreakerHeader>
              <H1>{breaker?.breakerName}</H1>
            </BreakerHeader>

            <PanelInfoRow>
              <PanelInfoColumn>
                <PanelInfoHeaderText>
                  <FormattedMessage {...messages.headerDeviceType} />
                </PanelInfoHeaderText>
                <PanelInfoText>{breaker?.type ?? '-'}</PanelInfoText>
              </PanelInfoColumn>
            </PanelInfoRow>
          </>
        )}
      </WhiteBlock>

      <BreakerGrid>
        <BreakerBlock>
          {isFetching ? (
            <SpinnerCentered />
          ) : (
            <>
              <PanelInfoColumn>
                <PanelInfoHeaderText>
                  <FormattedMessage {...messages.panel} />
                </PanelInfoHeaderText>
                <PanelInfoText>{panelData?.panelName}</PanelInfoText>
              </PanelInfoColumn>
              <PanelInfoColumn>
                <PanelInfoHeaderText>
                  <FormattedMessage {...messages.slotNumber} />
                </PanelInfoHeaderText>
                <PanelInfoText>{`${breaker?.slots[0]} - ${
                  breaker?.slots[breaker?.slots.length - 1]
                }`}</PanelInfoText>
              </PanelInfoColumn>
            </>
          )}
        </BreakerBlock>
        <BreakerBlock>
          {isFetching ? (
            <SpinnerCentered />
          ) : (
            <PanelInfoColumn>
              <PanelInfoHeaderText>
                <FormattedMessage {...messages.headerCircuitName} />
              </PanelInfoHeaderText>
              <PanelInfoText>{circuitName}</PanelInfoText>
            </PanelInfoColumn>
          )}
        </BreakerBlock>
      </BreakerGrid>
    </div>
  )
}

export default BreakerInfo
